import { Favorite } from "@/components/Icon/Favorite"
import { useWishlist } from "@/lib/store/wishlist"
import { useStores } from "@/stores"
import { cn, useI18n, usePdp, usePdpAdded, usePending } from "@jog/react-components"
import { gtmAddToWishlist, gtmRemoveFromWishlist } from "@jog/shared"
import { observer } from "mobx-react"
import { MouseEventHandler, useCallback, useMemo } from "react"

const Wishlist = () => {
    const { detail, productData } = usePdp()
    const addWishlistItem = useWishlist.use.addWishlistItem()
    const removeProductFromWishlist = useWishlist.use.removeWishlistItem()
    const favoriteProducts = useWishlist.use.simpleWishlist()?.lineItems
    const lineItemId = useMemo(
        () => favoriteProducts?.find((lineItem) => lineItem.productId === detail.ctpId)?.id,
        [detail, favoriteProducts]
    )

    const onFavorite = useCallback<MouseEventHandler>(
        async (e) => {
            e.preventDefault()
            const price = (productData?.firstPrice || productData?.price)?.centAmount || 0
            const gtmPrice = +(price / 100).toFixed(2)
            const gtmItem = { id: detail.gtmId, name: detail.name, price: gtmPrice }
            if (!lineItemId) {
                gtmAddToWishlist([gtmItem])
                await addWishlistItem(detail.ctpId)
            } else {
                gtmRemoveFromWishlist([gtmItem])
                await removeProductFromWishlist(lineItemId)
            }
        },
        [addWishlistItem, detail, lineItemId, productData, removeProductFromWishlist]
    )
    return (
        <button
            onClick={onFavorite}
            className="flex h-12 w-12 items-center justify-center border border-black"
            aria-label={lineItemId ? "Remove from wishlist" : "Add to wishlist"}>
            <Favorite
                width="32"
                height="32"
                className={cn("text-green-dark", lineItemId ? "fill-green-dark" : "fill-white")}
            />
        </button>
    )
}
export const AddToCart = observer((props: React.HTMLAttributes<HTMLDivElement>) => {
    const { cartStore } = useStores()
    const { sku } = usePdp()
    const onAdd = usePdpAdded()

    const { setPending } = usePending()
    const addProductToCart = async () => {
        if (!sku) {
            const $size = document.querySelector(`#sizeSelector`)
            if ($size) {
                $size.scrollIntoView({ behavior: "smooth", block: "center" })
            }
            return
        }
        if (cartStore.isCtaDisabled) return
        setPending(true)
        await cartStore.addProductsToCart([{ sku, quantity: 1 }]).finally(() => setPending(false))
        onAdd()
    }

    const { $t } = useI18n()

    return (
        <div {...props}>
            <div className="grid grid-cols-[1fr,auto] gap-x-3">
                <button
                    data-test="add-to-cart-button"
                    onClick={addProductToCart}
                    className="group flex h-12 w-full items-center justify-center bg-orange-main text-white hover:bg-green-main focus:outline-none md:w-auto">
                    <span className="relative mr-4 w-4">
                        <svg
                            className="absolute top-1/2 left-0 -translate-y-1/2 duration-300 group-hover:left-2"
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="16"
                            viewBox="0 0 10 16"
                            fill="none">
                            <path d="M1.5 2L7.5 8L1.5 14" stroke="white" strokeWidth="3" />
                        </svg>
                    </span>
                    <span className="font-primary text-base font-bold uppercase">{$t("Add To Cart")}</span>
                </button>
                <Wishlist />
            </div>
        </div>
    )
})
